import { useState } from 'react';
import { addNoteLink, emptyNote, flagPropagation, noteFoldedWords, noteFromSurface, noteIsRich, noteProblems, noteSaveShape, noteTextToStore, noteWords, revealsCallFields, richTextFromPlain, sanitizeNoteText, Size, t, type Chip, type FlagEvent, type NoteDirection, type NoteDraft, type SearchFn } from '@bms2/shared';
import { Chips, NumericInput, RadioGroup, RichTextEditor, Select, Textarea, ToggleSwitch } from '../controls';
import { Modal } from './Modal';
import { useAddNew } from '../../pages/records/AddNewHost';
import { ModalField } from './ModalShell';
import styles from './modal.module.css';
import noteStyles from './NoteModal.module.css';

/**
 * The Add/Edit Note modal (piece 5; decisions 0175, 0204). Every record's
 * Internal Notes uses this one modal, and no other screen in the plan edits a
 * Note — so `issueFlagId`, `activityKindId`, the call pair and
 * `confidentialityRoleId` have their writer here or nowhere. An unwritten
 * `activityKindId` leaves the logged-activity figure of Lead funnel
 * permanently empty.
 *
 * Author and dateTime are not fields: the modal sets neither and the server
 * writes both on save.
 */
export const noteModalDocumentedStates = ['empty', 'call', 'flagged'] as const;

export interface NamedRow {
  id: string;
  name: string;
}

export interface NoteModalProps {
  open: boolean;
  /** Which record the note hangs off, for the Title Bar's context line. */
  subtitle?: string;
  initial?: NoteDraft;
  /** The title the Title Bar shows; Edit Note when the modal reopens on a note its author wrote (decision 0301). */
  title?: string;
  /** The configured IssueFlag rows; the control offers these and takes no typed value. */
  issueFlags: readonly NamedRow[];
  /** The seeded ActivityKind rows of Reference data: Call, Meeting, Site visit, Email logged. */
  activityKinds: readonly NamedRow[];
  /** The role list of The permission model, stubbed here and stored as handed. */
  confidentialityRoles: readonly NamedRow[];
  /**
   * The search behind Also about, supplied by the page: the global search, each
   * row's id carrying the record type in front of it as `Type:id` (decision 0339).
   */
  searchRecords: SearchFn;
  /** The record the note is written on, as the fixed chip reads it: the kind and the name. */
  originLabel?: string;
  onSave: (note: NoteDraft) => void;
  onCancel: () => void;
  /**
   * The flag event, raised with no recipient named: the Overdue and Reminder
   * Engine already holds the record's targets, and a second way of deciding
   * who hears about a record would duplicate the first.
   */
  onFlagRaised?: (event: FlagEvent) => void;
  recordType?: string;
  recordId?: string;
  openerId?: string | null;
}

/** A chosen row back into a link: the id carries the type in front of the record's own id. */
function linkFromChip(chip: Chip) {
  const at = chip.id.indexOf(':');
  return { recordType: at > 0 ? chip.id.slice(0, at) : '', recordId: at > 0 ? chip.id.slice(at + 1) : chip.id, label: chip.label };
}

export function NoteModal({
  open,
  subtitle,
  initial,
  title,
  issueFlags,
  activityKinds,
  confidentialityRoles,
  searchRecords,
  originLabel,
  onSave,
  onCancel,
  onFlagRaised,
  recordType = '',
  recordId = '',
  openerId = null,
}: NoteModalProps) {
  /** 🔒 Add New on Also about (T311; decision 0874): which kind of record first, then its form; the saved record joins the note's links. */
  const addNew = useAddNew();
  const [draft, setDraft] = useState<NoteDraft>(() => {
    const opened = initial ?? emptyNote();
    // The origin is the page's record unless the draft already names one, as an edit from a linked record does.
    const withOrigin = opened.taggedRecordId ? opened : { ...opened, taggedRecordType: recordType || null, taggedRecordId: recordId || null };
    // A link that names the origin is dropped rather than shown twice.
    // 🔒 Decision 7290: the switch opens on, so a plain note's words open in the editor as a paragraph a line.
    const bare: NoteDraft = { ...withOrigin, body: noteIsRich(withOrigin.body) ? withOrigin.body : richTextFromPlain(withOrigin.body), links: [] };
    return withOrigin.links.reduce<NoteDraft>((d, link) => addNoteLink(d, link), bare);
  });
  const set = <K extends keyof NoteDraft>(field: K, value: NoteDraft[K]) => setDraft((d) => ({ ...d, [field]: value }));
  /**
   * 🔒 The Rich text switch (decisions 7180, 7290): on, the Rich Text Editor; off, the plain box, whose paste keeps the
   * words only, the draft carried across either way. It opens on for every note, new or old, and a person turns it off;
   * nothing remembers it. A save with no formatting in it is stored as plain words.
   */
  const [rich, setRich] = useState(true);
  const switchRich = (on: boolean) => {
    setRich(on);
    setDraft((d) => ({ ...d, body: on ? (noteIsRich(d.body) ? d.body : richTextFromPlain(d.body)) : noteWords(d.body) }));
  };
  const kindName = activityKinds.find((k) => k.id === draft.activityKindId)?.name ?? null;
  const showsCall = revealsCallFields(kindName);
  const problems = noteProblems(draft);

  const save = () => {
    if (problems.length > 0) return;
    const note = noteSaveShape(rich ? { ...draft, body: noteTextToStore(noteFromSurface(draft.body)) } : draft, kindName);
    onSave(note);
    const propagation = flagPropagation(note);
    if (propagation.notification && note.issueFlagId && onFlagRaised) {
      onFlagRaised({ recordType, recordId, issueFlagId: note.issueFlagId });
    }
    onCancel();
  };

  const modalProps = {
    open,
    title: title ?? t('note.title'),
    onCancel,
    openerId,
    dataModal: 'note',
    actions: [
      { kind: 'primary' as const, label: t('note.save'), onAct: save, disabled: problems.length > 0 },
      { kind: 'cancel' as const, label: t('modal.cancel'), onAct: onCancel },
    ],
    ...(subtitle === undefined ? {} : { subtitle }),
  };

  return (
    <Modal {...modalProps}>
      <div className={noteStyles.richRow} data-note-field="richText" data-note-rich={rich ? 'on' : 'off'}>
        <span className={noteStyles.richSwitch}>
          <span className={noteStyles.richLabel}>{t('note.richText')}</span>
          <ToggleSwitch checked={rich} onChange={switchRich} name={t('note.richText')} />
        </span>
        <p className={noteStyles.hint}>{t('note.richTextHint')}</p>
        {/* 🔒 Decision 7180: before the switch reduces a formatted note to words, the window says so, as the phone's edit does. */}
        {rich && noteIsRich(draft.body) && noteFoldedWords(noteFromSurface(draft.body)) === null ? <p className={noteStyles.hint} data-note-rich-off-hint="true">{t('note.richTextOffHint')}</p> : null}
      </div>
      {rich ? (
        // 🔒 The text set: the marks, the lists and the alignments, no picture; a paste passes the notes' allowance (decision 7180).
        <RichTextEditor label={t('note.body')} value={draft.body} onChange={(v) => set('body', v)} textOnly sanitize={sanitizeNoteText} sanitizePaste={sanitizeNoteText} name="noteBody" />
      ) : (
        <Textarea size={Size.Large} label={t('note.body')} value={draft.body} onChange={(v) => set('body', v)} required rows={4} />
      )}

      {/* 🔨 The note is tagged to the record it is written on, fixed; Also about adds NoteRecordLink rows and never moves it (decision 0339). */}
      <ModalField label={t('note.about')}>
        <span className={styles.chip} data-note-field="taggedRecord" data-tagged-record={draft.taggedRecordId ?? recordId} data-tagged-record-type={draft.taggedRecordType ?? recordType}>
          {originLabel ?? draft.taggedRecordId ?? recordId}
        </span>
      </ModalField>
      <div data-note-field="links" data-note-links={draft.links.length}>
        <Chips
          label={t('note.alsoAbout')}
          entityLabel={t('note.recordEntity')}
          value={draft.links.map((link): Chip => ({ id: `${link.recordType}:${link.recordId}`, label: link.label }))}
          onChange={(next) => setDraft((d) => ({ ...d, links: next.map((chip) => d.links.find((link) => `${link.recordType}:${link.recordId}` === chip.id) ?? linkFromChip(chip)) }))}
          search={searchRecords}
          onAddNew={() => addNew.open({ kind: 'choose', types: ['CompanyRecord', 'PersonRecord', 'LocationRecord', 'Deal'], typedIds: true }, (row) => {
            const link = linkFromChip({ id: row.id, label: row.line1 });
            setDraft((d) => addNoteLink(d, link));
          })}
        />
        <p className={noteStyles.hint}>{t('note.alsoAboutHint')}</p>
      </div>

      <Select size={Size.Medium}
        label={t('note.issueFlag')}
        value={draft.issueFlagId ?? ''}
        onChange={(next) => set('issueFlagId', next === '' ? null : next)}
        placeholder={t('note.none')}
        options={issueFlags.map((flag) => ({ value: flag.id, label: flag.name }))}
        dataAttribute={{ 'data-note-field': 'issueFlag' }}
      />

      <Select size={Size.Medium}
        label={t('note.activityKind')}
        value={draft.activityKindId ?? ''}
        onChange={(next) => set('activityKindId', next === '' ? null : next)}
        placeholder={t('note.none')}
        options={activityKinds.map((kind) => ({ value: kind.id, label: kind.name }))}
        dataAttribute={{ 'data-note-field': 'activityKind' }}
      />

      {showsCall ? (
        <>
          <RadioGroup
            name="note-direction"
            legend={t('note.direction')}
            options={[
              { value: 'Inbound', label: t('note.inbound') },
              { value: 'Outbound', label: t('note.outbound') },
            ]}
            value={draft.direction}
            onChange={(v) => set('direction', v as NoteDirection)}
          />
          <NumericInput size={Size.Small} label={t('note.duration')} kind="number" value={draft.durationMinutes} onChange={(v) => set('durationMinutes', v)} />
        </>
      ) : null}

      <Select size={Size.Medium}
        label={t('note.confidentiality')}
        value={draft.confidentialityRoleId ?? ''}
        onChange={(next) => set('confidentialityRoleId', next === '' ? null : next)}
        placeholder={t('note.none')}
        options={confidentialityRoles.map((role) => ({ value: role.id, label: role.name }))}
        dataAttribute={{ 'data-note-field': 'confidentiality' }}
      />

      {draft.issueFlagId ? (
        <p className={styles.band} data-note-flag-banner="true">
          {t('note.flagBanner')} {issueFlags.find((f) => f.id === draft.issueFlagId)?.name ?? ''}
        </p>
      ) : null}
      {addNew.host}
    </Modal>
  );
}
